import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Client, type Room } from "@colyseus/sdk";
import { api, type User } from "./api";
import { AdminPage } from "./portal/AdminPage";
import { CommunityPanel } from "./portal/CommunityPanel";
import { GameCatalog } from "./portal/GameCatalog";
import { PortalHeader } from "./portal/PortalHeader";
import { usePortalRealtime } from "./portal/usePortalRealtime";
import type { SocialRoomCard } from "../shared/portal";

const TanksPage = lazy(() => import("./games/tanks/TanksPage"));
const ScrapZonePage = lazy(() => import("./games/scrap-zone/ScrapZonePage"));
const DotsPage = lazy(() => import("./games/dots/DotsPage"));
const LastBidPage = lazy(() => import("./games/last-bid/LastBidPage"));
const NumberSeedsPage = lazy(() => import("./games/number-seeds/NumberSeedsPage"));
const GAME_SESSION_KEY = "omrGameSession";
const LEGACY_BATTLE_TOKEN_KEY = "tanksReconnectionToken";

interface StoredGameSession {
  token: string;
  path: "/games/tanks" | "/games/scrap-zone" | "/games/dots" | "/games/last-bid";
}

function loadGameSession(): StoredGameSession | undefined {
  try {
    const value = sessionStorage.getItem(GAME_SESSION_KEY);
    if (value) {
      const parsed = JSON.parse(value) as Partial<StoredGameSession>;
      if (typeof parsed.token === "string" && (parsed.path === "/games/tanks" || parsed.path === "/games/scrap-zone" || parsed.path === "/games/dots" || parsed.path === "/games/last-bid")) return parsed as StoredGameSession;
    }
    const legacy = sessionStorage.getItem(LEGACY_BATTLE_TOKEN_KEY);
    return legacy ? { token: legacy, path: "/games/tanks" } : undefined;
  } catch { return undefined; }
}

function saveGameSession(session?: StoredGameSession): void {
  try {
    sessionStorage.removeItem(LEGACY_BATTLE_TOKEN_KEY);
    if (session) sessionStorage.setItem(GAME_SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(GAME_SESSION_KEY);
  } catch { /* Reconnect remains available until this tab closes. */ }
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Неизвестная ошибка";
}

function AuthScreen({ onAuthenticated }: { onAuthenticated: (user: User) => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      const result = await api<{ user: User }>(`/api/${mode}`, {
        method: "POST",
        body: JSON.stringify({ username: data.get("username"), password: data.get("password"), invite: data.get("invite") }),
      });
      onAuthenticated(result.user);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setBusy(false);
    }
  }

  return <main className="auth-shell">
    <section className="auth-card panel">
      <div className="auth-brand"><strong>OMR<span>_</span></strong><div><b>One More Round</b><small>Just one more.</small></div></div>
      <p className="lead">Игровой портал для коротких сетевых партий со своими.</p>
      <div className="tab-row"><button className={mode === "login" ? "active" : "ghost"} onClick={() => setMode("login")}>Вход</button><button className={mode === "register" ? "active" : "ghost"} onClick={() => setMode("register")}>По приглашению</button></div>
      <form onSubmit={submit}>
        {mode === "register" && <label>Приглашение<input name="invite" autoComplete="one-time-code" required /></label>}
        <label>Позывной<input name="username" autoComplete="username" minLength={3} maxLength={24} required /></label>
        <label>Пароль<input name="password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={10} required /></label>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary wide" disabled={busy}>{busy ? "СВЯЗЬ…" : mode === "login" ? "ВОЙТИ В OMR" : "СОЗДАТЬ ПРОФИЛЬ"}</button>
      </form>
      <p className="microcopy">Закрытый доступ · регистрация только по одноразовому коду</p>
    </section>
  </main>;
}

function usePathname(): [string, (path: string) => void] {
  const [path, setPath] = useState(window.location.pathname);
  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const navigate = useCallback((nextPath: string) => {
    if (window.location.pathname !== nextPath) window.history.pushState({}, "", nextPath);
    setPath(nextPath);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  return [path, navigate];
}

export function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [room, setRoom] = useState<Room | null>(null);
  const [roomPath, setRoomPath] = useState<StoredGameSession["path"] | null>(null);
  const [error, setError] = useState("");
  const [path, navigate] = usePathname();
  const client = useMemo(() => new Client(window.location.origin), []);

  const expireSession = useCallback(() => {
    saveGameSession();
    setRoom((activeRoom) => {
      if (activeRoom) void activeRoom.leave().catch(() => undefined);
      return null;
    });
    setRoomPath(null);
    setUser(null);
  }, []);
  const realtime = usePortalRealtime(client, user ?? null, expireSession);

  useEffect(() => {
    api<{ user: User }>("/api/me").then((result) => setUser(result.user)).catch(() => setUser(null));
  }, []);

  const activateRoom = useCallback((activeRoom: Room, gamePath: StoredGameSession["path"]) => {
    saveGameSession({ token: activeRoom.reconnectionToken, path: gamePath });
    activeRoom.onReconnect(() => saveGameSession({ token: activeRoom.reconnectionToken, path: gamePath }));
    activeRoom.onLeave(() => {
      setRoom((current) => {
        if (current !== activeRoom) return current;
        saveGameSession();
        setRoomPath(null);
        return null;
      });
    });
    setRoomPath(gamePath);
    setRoom(activeRoom);
  }, []);

  useEffect(() => {
    if (!user || room) return;
    const session = loadGameSession();
    if (!session) return;
    client.reconnect(session.token).then((reconnected) => {
      activateRoom(reconnected, session.path);
      navigate(session.path);
    }).catch(() => saveGameSession());
  }, [activateRoom, client, navigate, room, user]);

  const join = useCallback(async (roomId: string, gamePath: StoredGameSession["path"]) => {
    setError("");
    try {
      const { ticket } = await api<{ ticket: string }>("/api/realtime-ticket", { method: "POST" });
      activateRoom(await client.joinById(roomId, { ticket }), gamePath);
    } catch (joinError) {
      setError(errorText(joinError));
    }
  }, [activateRoom, client]);

  const clearRoom = useCallback(() => {
    saveGameSession();
    setRoom(null);
    setRoomPath(null);
  }, []);

  const navigateFromHeader = useCallback((nextPath: string) => {
    if (room && nextPath !== roomPath) {
      saveGameSession();
      setRoom(null);
      setRoomPath(null);
      void room.leave().catch(() => undefined);
    }
    navigate(nextPath);
  }, [navigate, room, roomPath]);

  const joinSocial = async (card:SocialRoomCard,validate:()=>Promise<SocialRoomCard|undefined>) => {
    if(room?.roomId===card.roomId)return;
    if(!room&&realtime.snapshot.online.some(p=>p.userId===user?.id&&p.status!=="portal"))throw Error("Вы уже участвуете в комнате в другой вкладке. Сначала выйдите из неё.");
    if((room||path.startsWith("/games/number-seeds"))&&!window.confirm(room?"Вы уже в другой комнате или матче. Покинуть её и перейти по приглашению? Текущее участие будет завершено.":"Перейти из одиночной игры в комнату? Прогресс «Семечек» сохраняется."))return;
    const current=await validate();if(!current)return;
    const target=({tanks:"/games/tanks","scrap-zone":"/games/scrap-zone",dots:"/games/dots","last-bid":"/games/last-bid"} as const)[current.gameId as "tanks"|"scrap-zone"|"dots"|"last-bid"];
    if(!target)throw Error("Игра недоступна");
    if(document.fullscreenElement)await document.exitFullscreen().catch(()=>undefined);
    if(room){saveGameSession();await room.leave();clearRoom();}
    const {ticket}=await api<{ticket:string}>("/api/realtime-ticket",{method:"POST"});
    const joined=await client.joinById(current.roomId,{ticket});
    activateRoom(joined,target);navigate(target);
  };

  useEffect(() => {
    if (!room || !roomPath || path.startsWith(roomPath)) return;
    saveGameSession();
    setRoom(null);
    setRoomPath(null);
    void room.leave().catch(() => undefined);
  }, [path, room, roomPath]);

  if (user === undefined) return <main className="loading"><div className="pixel-loader" />Подключаем One More Round…</main>;
  if (!user) return <AuthScreen onAuthenticated={setUser} />;

  return <div className="portal-app">
    <CommunityPanel key={user.id} user={user} realtime={realtime} roomId={room?.roomId} onJoin={joinSocial}/>
    {error && <div className="toast error" role="alert">{error}</div>}
    <PortalHeader user={user} currentPath={path} onNavigate={navigateFromHeader} onLogout={async () => {
      await api("/api/logout", { method: "POST" });
      expireSession();
    }} />
    {path === "/admin" && <AdminPage currentUser={user} />}
    {path.startsWith("/games/number-seeds") && <Suspense fallback={<main className="loading">Открываем тетрадку…</main>}><NumberSeedsPage user={user} onAllGames={() => navigate("/")} /></Suspense>}
    {path.startsWith("/games/tanks") && <Suspense fallback={<main className="loading"><div className="pixel-loader" />Открываем «ММПХ, танчики»…</main>}><TanksPage user={user} room={roomPath === "/games/tanks" ? room : null} onJoin={(roomId) => void join(roomId, "/games/tanks")} onLeaveRoom={clearRoom} onAllGames={() => navigate("/")} /></Suspense>}
    {path.startsWith("/games/scrap-zone") && <Suspense fallback={<main className="loading"><div className="pixel-loader" />Открываем Scrap Zone…</main>}><ScrapZonePage user={user} room={roomPath === "/games/scrap-zone" ? room : null} onJoin={(roomId) => void join(roomId, "/games/scrap-zone")} onLeaveRoom={clearRoom} onAllGames={() => navigate("/")} /></Suspense>}
    {path.startsWith("/games/dots") && <Suspense fallback={<main className="loading">Открываем долину…</main>}><DotsPage user={user} room={roomPath === "/games/dots" ? room : null} onJoin={(id) => void join(id, "/games/dots")} onLeaveRoom={clearRoom} onAllGames={() => navigate("/")} /></Suspense>}
    {path.startsWith("/games/last-bid") && <Suspense fallback={<main className="loading">Открываем аукционный зал…</main>}><LastBidPage user={user} room={roomPath==="/games/last-bid"?room:null} onJoin={id=>void join(id,"/games/last-bid")} onLeaveRoom={clearRoom} onAllGames={()=>navigate("/")}/></Suspense>}
    {path !== "/admin" && !path.startsWith("/games/tanks") && !path.startsWith("/games/scrap-zone") && !path.startsWith("/games/dots") && !path.startsWith("/games/number-seeds") && !path.startsWith("/games/last-bid") && <main className="portal-home"><div className="portal-grid"><GameCatalog onPlay={navigate} /></div></main>}
  </div>;
}
